import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Calendar, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const ContactAlt = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', formData);
      toast({
        title: "BOOKING RECEIVED!",
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        budget: '',
        timeline: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Header */}
      <section className="py-20 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-8xl lg:text-[12vw] font-black uppercase leading-none mb-8">
              LET'S
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
                CREATE
              </span>
            </h1>
            <p className="text-2xl font-semibold max-w-2xl">
              Book a consultation to discuss your next commercial filmmaking project
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-12"
              >
                <div>
                  <h2 className="text-4xl font-black uppercase mb-8">REACH OUT</h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    Whether you have a specific project in mind or just want to explore possibilities, we'd love to hear from you.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-black flex items-center justify-center flex-shrink-0">
                      <Mail className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">EMAIL</div>
                      <a href="mailto:hello@filmcraft.com" className="text-xl font-bold hover:text-orange-500 transition-colors">
                        hello@filmcraft.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-black flex items-center justify-center flex-shrink-0">
                      <Phone className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">PHONE</div>
                      <a href="tel:+1234567890" className="text-xl font-bold hover:text-orange-500 transition-colors">
                        (123) 456-7890
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-black flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">LOCATION</div>
                      <div className="text-xl font-bold">Los Angeles, CA</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-black flex items-center justify-center flex-shrink-0">
                      <Calendar className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">HOURS</div>
                      <div className="text-lg font-bold">
                        <div>Mon-Fri: 9AM - 6PM</div>
                        <div className="text-gray-500">Weekend: By Appointment</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Booking Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <form onSubmit={handleSubmit} className="bg-gray-50 p-8 lg:p-12">
                <h3 className="text-3xl font-black uppercase mb-8">BOOK A CALL</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider mb-2 block">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="border-2 border-black focus:border-orange-500 h-12 text-base font-semibold"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="border-2 border-black focus:border-orange-500 h-12 text-base font-semibold"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider mb-2 block">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="border-2 border-black focus:border-orange-500 h-12 text-base font-semibold"
                      placeholder="(123) 456-7890"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-xs font-bold uppercase tracking-wider mb-2 block">Company</Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="border-2 border-black focus:border-orange-500 h-12 text-base font-semibold"
                      placeholder="Company Name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="projectType" className="text-xs font-bold uppercase tracking-wider mb-2 block">Project Type *</Label>
                    <Select
                      value={formData.projectType}
                      onValueChange={(value) => handleSelectChange('projectType', value)}
                      required
                    >
                      <SelectTrigger className="border-2 border-black focus:border-orange-500 h-12 text-base font-semibold">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-2 border-black">
                        <SelectItem value="brand-film" className="font-semibold">Brand Film</SelectItem>
                        <SelectItem value="commercial" className="font-semibold">Commercial</SelectItem>
                        <SelectItem value="podcast" className="font-semibold">Podcast Production</SelectItem>
                        <SelectItem value="real-estate" className="font-semibold">Real Estate Showcase</SelectItem>
                        <SelectItem value="testimonial" className="font-semibold">Testimonial Video</SelectItem>
                        <SelectItem value="social-media" className="font-semibold">Social Media Content</SelectItem>
                        <SelectItem value="other" className="font-semibold">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="timeline" className="text-xs font-bold uppercase tracking-wider mb-2 block">Timeline</Label>
                    <Select
                      value={formData.timeline}
                      onValueChange={(value) => handleSelectChange('timeline', value)}
                    >
                      <SelectTrigger className="border-2 border-black focus:border-orange-500 h-12 text-base font-semibold">
                        <SelectValue placeholder="Select timeline" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-2 border-black">
                        <SelectItem value="urgent" className="font-semibold">ASAP (Rush)</SelectItem>
                        <SelectItem value="1-month" className="font-semibold">Within 1 Month</SelectItem>
                        <SelectItem value="2-3-months" className="font-semibold">2-3 Months</SelectItem>
                        <SelectItem value="flexible" className="font-semibold">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="budget" className="text-xs font-bold uppercase tracking-wider mb-2 block">Budget Range</Label>
                  <Select
                    value={formData.budget}
                    onValueChange={(value) => handleSelectChange('budget', value)}
                  >
                    <SelectTrigger className="border-2 border-black focus:border-orange-500 h-12 text-base font-semibold">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-2 border-black">
                      <SelectItem value="5k-10k" className="font-semibold">$5,000 - $10,000</SelectItem>
                      <SelectItem value="10k-25k" className="font-semibold">$10,000 - $25,000</SelectItem>
                      <SelectItem value="25k-50k" className="font-semibold">$25,000 - $50,000</SelectItem>
                      <SelectItem value="50k+" className="font-semibold">$50,000+</SelectItem>
                      <SelectItem value="flexible" className="font-semibold">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="mb-8">
                  <Label htmlFor="message" className="text-xs font-bold uppercase tracking-wider mb-2 block">Tell Us About Your Project *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    className="border-2 border-black focus:border-orange-500 min-h-32 text-base font-semibold"
                    placeholder="Share your vision, goals, and any other relevant details..."
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white hover:bg-gray-900 h-14 text-base font-black uppercase"
                >
                  {isSubmitting ? (
                    'SENDING...'
                  ) : (
                    <>
                      BOOK NOW
                      <Send className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-6xl lg:text-7xl font-black uppercase mb-16"
          >
            FAQ
          </motion.h2>
          <div className="space-y-8">
            {[
              {
                q: 'WHAT IS YOUR TYPICAL PROJECT TIMELINE?',
                a: 'Most projects take 4-8 weeks from concept to final delivery, depending on complexity. We can accommodate rush projects with advance notice.'
              },
              {
                q: 'DO YOU TRAVEL FOR SHOOTS?',
                a: 'Yes! We regularly travel for projects. Travel expenses are included in the project quote based on location and duration.'
              },
              {
                q: 'WHAT EQUIPMENT DO YOU USE?',
                a: 'We use professional cinema cameras, lighting, and audio equipment. Specific gear is selected based on each project\'s unique requirements.'
              },
              {
                q: 'CAN YOU HELP WITH SCRIPTING?',
                a: 'Absolutely. We offer full pre-production services including concept development, scriptwriting, and storyboarding.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="border-t border-gray-800 pt-6"
              >
                <h3 className="text-xl font-black mb-3">{faq.q}</h3>
                <p className="text-gray-400 text-lg leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactAlt;